import type { ApiEnvelope } from '@/types';

declare global {
  interface Window {
    __BP_API_URL__: string;
    __BP_KEYCLOAK_URL__: string;
  }
}

function getBaseUrl(): string {
  if (import.meta.env.DEV) return '/api/v1';
  if (typeof window !== 'undefined' && window.__BP_API_URL__) {
    return window.__BP_API_URL__;
  }
  return import.meta.env.VITE_API_URL || '/api/v1';
}

const TOKEN_KEY = 'bp_session_token';
const EXPIRES_KEY = 'bp_session_expires';

let authToken: string | null = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

let sessionExpiresAt: string | null = (() => {
  try {
    return localStorage.getItem(EXPIRES_KEY);
  } catch {
    return null;
  }
})();

export function setAuthToken(token: string | null, expiresAt?: string): void {
  authToken = token;
  if (expiresAt) sessionExpiresAt = expiresAt;
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      if (expiresAt) localStorage.setItem(EXPIRES_KEY, expiresAt);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EXPIRES_KEY);
      sessionExpiresAt = null;
    }
  } catch {
    // storage full or blocked — degrade to in-memory only
  }
}

export function getAuthToken(): string | null {
  return authToken;
}

export function getSessionExpiresAt(): string | null {
  return sessionExpiresAt;
}

interface RequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  params?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const { params, headers: extraHeaders, ...rest } = options;

  // Path is passed with a leading slash (e.g. "/workseeking/search/candidates").
  // An absolute path would discard the base URL's "/api/v1" prefix, so strip the
  // leading slash and ensure the base ends with one before joining.
  const base = getBaseUrl();
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  // Relative bases (e.g. "/api/v1" in dev) must be made absolute for new URL().
  const absoluteBase = base.startsWith('http') ? base : `${window.location.origin}${base}`;
  const url = new URL(cleanPath, absoluteBase.endsWith('/') ? absoluteBase : `${absoluteBase}/`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(extraHeaders as Record<string, string>),
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      ...rest,
    });
  } catch (err) {
    console.error(`[api] ${method} ${url.pathname}${url.search} network error:`, err);
    throw err;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    console.error(`[api] ${method} ${url.pathname}${url.search} ${response.status}:`, errorBody);
    throw new ApiError(response.status, response.statusText, errorBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly detail: string;

  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: string,
  ) {
    const parsed = ApiError.parseBody(body);
    super(parsed.message || `API Error ${status}`);
    this.name = 'ApiError';
    this.statusCode = status;
    this.detail = parsed.detail;
  }

  private static parseBody(body: string): { message: string; detail: string } {
    try {
      const json = JSON.parse(body);
      // NestJS error envelope: { statusCode, message, error }
      // or validation: { statusCode, message: string[], error }
      const msg = Array.isArray(json.message) ? json.message[0] : json.message;
      return { message: msg || json.error || '', detail: json.detail || '' };
    } catch {
      return { message: body || '', detail: '' };
    }
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),

  upload: <T>(path: string, formData: FormData, options?: RequestOptions) => {
    const { params, headers: extraHeaders, ...rest } = options ?? {};
    const base = getBaseUrl();
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const absoluteBase = base.startsWith('http') ? base : `${window.location.origin}${base}`;
    const url = new URL(cleanPath, absoluteBase.endsWith('/') ? absoluteBase : `${absoluteBase}/`);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
      }
    }
    const headers: Record<string, string> = { ...(extraHeaders as Record<string, string>) };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    // Do NOT set Content-Type — browser sets multipart boundary automatically
    return fetch(url.toString(), { method: 'POST', headers, body: formData, ...rest })
      .then(async (res) => {
        if (!res.ok) {
          const errBody = await res.text().catch(() => '');
          throw new ApiError(res.status, res.statusText, errBody);
        }
        return res.json() as Promise<T>;
      });
  },
};

export type { ApiEnvelope };
